import React, { useCallback, useState } from 'react';
import { ImageStyle, StyleProp } from 'react-native';
import PencilIcon from '@shared/assets/icons/v2/pencil.svg';
import TrashIcon from '@shared/assets/icons/v2/trash.svg';
import { View } from '@shared/components/View';
import { MBAnimation, MBAnimationFadeIn } from '@shared/components/animation';
import { MBHover } from '@shared/components/hover';
import { MBIcon, MBImageURISource } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBMessageReactionButton } from '@shared/scenes/chat/thread/containers/messageReactionButton';
import { MBChannelTypeUtil } from '@shared/scenes/chat/thread/util/channelType';
import { Colors } from '@shared/styles';
import { MBAlert } from '@shared/util/alert';
import { i18n } from '@shared/util/i18n';

export interface Props {
  message: MBAPI.ChatMessage;
  channelSlug: string;
  isHovering: boolean;
  isMyMessage: boolean;
  onDelete(): void;
  onEdit(): void;
  includeEdit?: boolean;
  isSent?: boolean;
}

interface ButtonProps {
  onPress(): void;
  source: MBImageURISource;
  hoverColor?: string;
  style?: StyleProp<ImageStyle>;
}

// TODO(fant): consider abstracting this and putting it in @shared/components/button
function MBActionButton({ onPress, source, hoverColor, style }: ButtonProps) {
  const [isHovered, setIsHovered] = useState<boolean>(false);

  return (
    <MBHover onHoverStart={() => setIsHovered(true)} onHoverEnd={() => setIsHovered(false)}>
      <MBTouchableOpacity onPress={onPress} style={{ overflow: 'hidden' }}>
        <MBAnimation scale={{ from: 1, to: 1.15 }} disabled={!isHovered} type="spring">
          <MBIcon
            color={isHovered && !!hoverColor ? hoverColor : Colors.v2.gray30}
            source={source}
            style={style}
            noPadding
          />
        </MBAnimation>
      </MBTouchableOpacity>
    </MBHover>
  );
}

export function MBActionButtonsComponent({
  message,
  isHovering,
  isMyMessage,
  channelSlug,
  onEdit,
  onDelete,
  includeEdit = true,
  isSent = true,
}: Props) {
  const isThread = MBChannelTypeUtil.isThread(channelSlug);
  // TODO(nf): reactions won't work in threads https://app.shortcut.com/coast/story/30746/threads-fix-reaction-mutation-in-chat-threads
  const showReact =
    isSent && !isThread && (isHovering || (!message.meta.reactions.length && !isMyMessage));
  const showEdit = isSent && isHovering && isMyMessage && !!message.content.length && includeEdit;
  const showDelete = isHovering && isMyMessage;
  const hasAttachments = message.meta.attachments.length > 0;
  const deleteMessageConfirmation = hasAttachments
    ? i18n('chat.delete.confirmationMessageWithAttachments')
    : i18n('chat.delete.confirmation');

  const confirmDelete = useCallback(
    () =>
      MBAlert.confirm({
        title: i18n('chat.delete.title'),
        message: deleteMessageConfirmation,
        confirmText: i18n('chat.delete.confirmButton'),
        confirmStyle: 'destructive',
        onConfirm: onDelete,
      }),
    [onDelete, deleteMessageConfirmation],
  );

  if (!showReact && !showEdit && !showDelete) {
    return <View paddingHorizontal={1} />;
  }

  return (
    <MBAnimationFadeIn>
      <View row shrinkWrap centerVertically>
        {showReact && <MBMessageReactionButton message={message} channelSlug={channelSlug} />}
        {showEdit && (
          <MBActionButton onPress={onEdit} source={PencilIcon} style={{ width: 27, height: 27 }} />
        )}
        {showDelete && (
          <MBActionButton
            onPress={confirmDelete}
            hoverColor={Colors.v2.red}
            source={TrashIcon}
            style={{ width: 27, height: 27 }}
          />
        )}
      </View>
    </MBAnimationFadeIn>
  );
}
